import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { NotificationService } from '@core/services/notification.service';
import { AdminService } from '@generated/services/admin.service';
import { ModelsService } from '@generated/services/models.service';
import { GetModelsHierarchyResult, GatewayDto } from '@generated/models';
import { ModelSelector } from '@shared/components/model-selector/model-selector';

@Component({
  selector: 'app-model-edit',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    SelectModule,
    ProgressSpinnerModule,
    ModelSelector
],
  templateUrl: './model-edit.html',
  styleUrl: './model-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModelEditComponent implements OnInit, OnDestroy {
  private adminService = inject(AdminService);
  private modelsService = inject(ModelsService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  modelsHierarchy: GetModelsHierarchyResult | null = null;
  gateways: GatewayDto[] = [];

  // Filters
  modelTypeOptions: { label: string; value: string }[] = [];
  gatewayOptions: { label: string; value: string }[] = [];
  selectedModelType: string | null = null;
  selectedGatewayCode: string | null = null;

  // Cache for ModelSelector — stable reference, recomputed only on filter
  // change. Otherwise OnPush sees a fresh object every CD cycle and rebuilds
  // the list, losing in-flight tester state.
  filteredHierarchy: GetModelsHierarchyResult | null = null;
  modelTypesFilter: ('llm' | 'stt' | 'tts' | 'vad')[] = [];

  isLoading = true;
  isSyncing = false;
  /** Admin defaults to the full catalog (RAW prices, no filtering). The
   *  switch in the selector still works — turning it ON shows only the
   *  recommended subset; turning OFF triggers the warning dialog. */
  modelsIncludeAll = true;

  ngOnInit() {
    this.loadAll();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAll() {
    this.isLoading = true;
    forkJoin({
      // Admin endpoint returns RAW prices (no plan-coefficient multiplication)
      // — admins must see the rates they set, not what users would be charged.
      hierarchy: this.adminService.apiAdminModelsHierarchyGet({ includeAll: this.modelsIncludeAll }),
      gateways: this.modelsService.apiModelsGatewaysGet()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ hierarchy, gateways }) => {
          this.modelsHierarchy = hierarchy;
          this.gateways = gateways?.gateways || [];
          this.buildFilterOptions();
          this.rebuildFilteredHierarchy();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load admin data:', error);
          this.notificationService.showError($localize`:@@adminModels.loadFailed:Не удалось загрузить данные моделей`);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private buildFilterOptions() {
    this.modelTypeOptions = (this.modelsHierarchy?.model_types || [])
      .filter(t => t.code)
      .map(t => ({ label: t.name || t.code || '', value: t.code || '' }));

    this.gatewayOptions = (this.gateways || [])
      .filter(g => g.code)
      .map(g => ({ label: g.name || g.code || '', value: g.code || '' }));
  }

  private rebuildFilteredHierarchy(): void {
    if (!this.modelsHierarchy) {
      this.filteredHierarchy = null;
      return;
    }
    const filteredTypes = (this.modelsHierarchy.model_types || [])
      .filter(t => !this.selectedModelType || t.code === this.selectedModelType)
      .map(t => ({
        ...t,
        gateways: (t.gateways || []).filter(g => !this.selectedGatewayCode || g.code === this.selectedGatewayCode)
      }));
    this.filteredHierarchy = { model_types: filteredTypes };
    const code = this.selectedModelType?.toLowerCase() as any;
    this.modelTypesFilter = code ? [code] : [];
  }

  onFilterChange(): void {
    this.rebuildFilteredHierarchy();
    this.cdr.markForCheck();
  }

  private refreshHierarchy() {
    this.adminService.apiAdminModelsHierarchyGet({ includeAll: this.modelsIncludeAll })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hierarchy) => {
          this.modelsHierarchy = hierarchy;
          this.rebuildFilteredHierarchy();
          this.cdr.markForCheck();
        }
      });
  }

  onModelsIncludeAllChange(includeAll: boolean): void {
    this.modelsIncludeAll = includeAll;
    this.refreshHierarchy();
  }

  syncSelected() {
    if (!this.selectedModelType || !this.selectedGatewayCode) return;

    this.isSyncing = true;
    this.cdr.markForCheck();

    const code = this.selectedModelType.toLowerCase();
    const gatewayCode = this.selectedGatewayCode;
    const call = code === 'llm'
      ? this.adminService.apiAdminModelsLlmSyncPost({ gatewayCode })
      : code === 'stt'
        ? this.adminService.apiAdminModelsSttSyncPost({ gatewayCode })
        : code === 'tts'
          ? this.adminService.apiAdminModelsTtsSyncPost({ gatewayCode })
          : code === 'vad'
            ? this.adminService.apiAdminModelsVadSyncPost({ gatewayCode })
            : code === 'realtime'
              ? this.adminService.apiAdminModelsRealtimeSyncPost({ gatewayCode })
              : null;

    if (!call) {
      this.notificationService.showError($localize`:@@adminModels.syncUnsupported:Синхронизация не поддерживается для типа: ${code}:type:`);
      this.isSyncing = false;
      this.cdr.markForCheck();
      return;
    }

    call.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: any) => {
        const msg = result?.message || $localize`:@@adminModels.syncDone:Синхронизация ${code.toUpperCase()}:type: (${gatewayCode}:gateway:) завершена`;
        this.notificationService.showSuccess(msg);
        this.isSyncing = false;
        this.refreshHierarchy();
      },
      error: () => {
        this.notificationService.showError($localize`:@@adminModels.syncFailed:Не удалось синхронизировать ${code}:type: через ${gatewayCode}:gateway:`);
        this.isSyncing = false;
        this.cdr.markForCheck();
      }
    });
  }
}
